import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Card, Button, Empty, Grid, Row, Col, Typography, theme } from 'antd';
import { PlusOutlined, RightOutlined, DownOutlined } from '@ant-design/icons';

const { useBreakpoint } = Grid;
const { Text } = Typography;

/**
 * Conceptos de la factura, como filas plegables.
 *
 * El problema que resuelve: la fila intentaba mostrar todos los campos de un
 * concepto a la vez, aunque al editar solo se toque uno. Eso obligaba a moverse
 * en dos ejes en una factura de 20 conceptos —arriba a buscar el botón de
 * agregar, y a los lados porque las siete columnas sumaban 1010 px mínimos y la
 * tabla llevaba scroll horizontal.
 *
 * Plegado, cada concepto ocupa UNA línea: los 20 se repasan de un vistazo antes
 * de timbrar. Desplegado, sus campos caben en el ancho disponible.
 *
 * Los controles siguen viniendo de `columns`, que en la práctica ya era un
 * registro de campos por clave: así lo usaba la vista de móvil. Por eso este
 * rediseño no toca el cálculo de importes ni CreateInvoice.js.
 */

const campo = (columns, key) => columns.find((c) => c.key === key);

const dinero = (n) =>
  `$${Number(n || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Campos de un concepto, en dos columnas desde `md`.
 *
 * Una sola columna dejaría el editor del doble de alto y traería de vuelta el
 * scroll vertical que el plegado viene a evitar. Producto, descripción e
 * impuestos van a ancho completo: los dos primeros son texto largo y el selector
 * de impuestos crece con las etiquetas — que es justo lo que forzaba parte del
 * desplazamiento horizontal de la tabla.
 */
function ConceptoEditor({ columns, item }) {
  const { token } = theme.useToken();
  const producto = campo(columns, 'product');
  const descripcion = campo(columns, 'description');
  const cantidad = campo(columns, 'quantity');
  const precio = campo(columns, 'price');
  const impuestos = campo(columns, 'taxes');

  const etiqueta = (texto) => (
    <div style={{ fontSize: 11, color: token.colorTextTertiary, marginBottom: 2 }}>{texto}</div>
  );

  return (
    <div style={{ padding: 12, background: token.colorFillQuaternary, borderRadius: 4 }}>
      <Row gutter={[12, 12]}>
        <Col xs={24}>
          {etiqueta(producto.title)}
          {producto.render(null, item)}
        </Col>

        <Col xs={24}>
          {etiqueta(descripcion.title)}
          {descripcion.render(item.description, item)}
        </Col>

        <Col xs={24} md={8}>
          {etiqueta(cantidad.title)}
          {cantidad.render(item.quantity, item)}
        </Col>

        <Col xs={24} md={16}>
          {etiqueta(precio.title)}
          {precio.render(item.price, item)}
        </Col>

        <Col xs={24}>
          {etiqueta(impuestos.title)}
          {impuestos.render(item.selectedTaxIds, item)}
        </Col>
      </Row>
    </div>
  );
}

/**
 * Un concepto: la línea plegada y, si está abierto, su editor.
 *
 * La línea plegada lleva lo justo para reconocer el concepto y detectar un error
 * de captura: qué es, cuántos, a cuánto y cuánto suma.
 */
function FilaConcepto({ columns, item, indice, abierto, onToggle, contenedorRef }) {
  const { token } = theme.useToken();
  const screens = useBreakpoint();
  const acciones = campo(columns, 'actions');

  const titulo = item.description?.trim() || item.product?.name || null;

  return (
    <div ref={contenedorRef} style={{ borderBottom: `1px solid ${token.colorSplit}`, padding: '8px 0' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Button
          type="text"
          size="small"
          aria-expanded={abierto}
          aria-label={`${abierto ? 'Plegar' : 'Desplegar'} concepto ${indice + 1}`}
          icon={abierto ? <DownOutlined /> : <RightOutlined />}
          onClick={() => onToggle(item.key)}
        />

        <Text type="secondary" style={{ minWidth: 18, fontSize: 12 }}>
          {indice + 1}
        </Text>

        {/* Lo único elástico de la línea, y se recorta: así cantidad e importe
            quedan alineados entre filas y el ojo los compara en vertical. */}
        <div
          onClick={() => onToggle(item.key)}
          style={{
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            cursor: 'pointer',
          }}
        >
          {titulo || <Text type="secondary">Concepto sin descripción</Text>}
        </div>

        {!screens.xs && (
          <Text type="secondary" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
            {item.quantity} × {dinero(item.price)}
          </Text>
        )}

        <Text strong style={{ minWidth: 90, textAlign: 'right', whiteSpace: 'nowrap' }}>
          {dinero((item.quantity || 0) * (item.price || 0))}
        </Text>

        {acciones.render(null, item)}
      </div>

      {abierto && (
        <div style={{ marginTop: 8 }}>
          <ConceptoEditor columns={columns} item={item} />
        </div>
      )}
    </div>
  );
}

export default function ItemsTableSection({ columns, invoiceItems, addItem }) {
  // CONJUNTO de claves abiertas, no una clave activa: un acordeón que cierra la
  // anterior impediría copiar un precio o un juego de impuestos de un concepto
  // al de al lado, que es tarea real en facturas repetitivas.
  const [abiertos, setAbiertos] = useState(() => new Set());

  const recienAgregado = useRef(false);
  const refsFilas = useRef({});

  const alternar = useCallback((key) => {
    setAbiertos((prev) => {
      const siguiente = new Set(prev);
      if (siguiente.has(key)) {
        siguiente.delete(key);
      } else {
        siguiente.add(key);
      }
      return siguiente;
    });
  }, []);

  const agregar = () => {
    recienAgregado.current = true;
    addItem();
  };

  /**
   * El concepto nuevo nace desplegado, enfocado y a la vista.
   *
   * Plegado no tiene campos que llenar: aparecería cerrado y exigiría un clic
   * extra en cada uno de los 20 conceptos, que es el gesto repetido que este
   * rediseño viene a quitar.
   *
   * `focus({ preventScroll: true })` y DESPUÉS scrollIntoView: focus() por su
   * cuenta desplaza donde el navegador decida. Con `block: 'nearest'` la fila
   * queda visible sin que la página dé un salto.
   */
  useEffect(() => {
    if (!recienAgregado.current || invoiceItems.length === 0) return;
    recienAgregado.current = false;

    const nuevo = invoiceItems[invoiceItems.length - 1];
    setAbiertos((prev) => new Set(prev).add(nuevo.key));

    // Tras el render que despliega la fila.
    requestAnimationFrame(() => {
      const fila = refsFilas.current[nuevo.key];
      if (!fila) return;

      const primerCampo = fila.querySelector('input, textarea');
      if (primerCampo) primerCampo.focus({ preventScroll: true });

      if (typeof fila.scrollIntoView === 'function') {
        fila.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    });
  }, [invoiceItems]);

  return (
    <Card
      title="Productos y Servicios"
      size="small"
      style={{ marginBottom: '12px' }}
      styles={{ body: { padding: '12px' } }}
      extra={
        invoiceItems.length > 0 ? (
          <Text type="secondary" style={{ fontSize: 12 }}>
            {invoiceItems.length} concepto{invoiceItems.length === 1 ? '' : 's'}
          </Text>
        ) : null
      }
    >
      {invoiceItems.length === 0 ? (
        <Empty
          description="No hay conceptos agregados"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          style={{ margin: '12px 0' }}
        />
      ) : (
        invoiceItems.map((item, indice) => (
          <FilaConcepto
            key={item.key}
            columns={columns}
            item={item}
            indice={indice}
            abierto={abiertos.has(item.key)}
            onToggle={alternar}
            contenedorRef={(el) => {
              refsFilas.current[item.key] = el;
            }}
          />
        ))
      )}

      {/* Al FINAL, que es donde está la mirada al terminar un concepto. Arriba
          obligaba a subir y volver a bajar una vez por concepto. */}
      <div style={{ marginTop: 12 }}>
        <Button
          type="dashed"
          onClick={agregar}
          icon={<PlusOutlined />}
          size="small"
          style={{ width: '100%' }}
        >
          Agregar concepto
        </Button>
      </div>
    </Card>
  );
}
